import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  Link as MuiLink,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

function InfoCard({ icon, title, bullets = [] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" mb={1}>
        <Box
          sx={(t) => ({
            width: 28,
            height: 28,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(t.palette.primary.main, 0.12),
            color: t.palette.primary.main,
            flexShrink: 0,
          })}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Stack>

      <Stack spacing={0.75} sx={{ color: "text.secondary" }}>
        {bullets.map((b, i) => (
          <Typography key={i} variant="body2">
            • {b}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
}

/**
 * Empty State do Chat
 * Props:
 * - userName?: string
 * - onNewConversation: () => void
 * - isCreating?: boolean  -> controla o loading do botão
 */
const EmptyState = ({ userName, onNewConversation, isCreating }) => {
  const { t } = useTranslation("chat");

  return (
    <Box
      className="tour-chat-empty"
      sx={(t) => ({
        minHeight: "calc(100vh - 120px)",
        display: "grid",
        placeItems: "start center",
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 4 },
        backgroundImage: `linear-gradient(${alpha(t.palette.primary.main, 0.06)}, transparent)`,
      })}
    >
      <Paper
        elevation={0}
        sx={(t) => ({
          width: "100%",
          maxWidth: 1080,
          borderRadius: 3,
          border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
          bgcolor: t.palette.background.paper,
        })}
      >
        {/* Hero */}
        <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box
              sx={(t) => ({
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(t.palette.primary.main, 0.12),
                color: t.palette.primary.main,
                flexShrink: 0,
              })}
            >
              <ChatRoundedIcon fontSize="medium" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" fontWeight={700} noWrap>
                {userName
                  ? t("emptyState.titleWithName", { userName })
                  : t("emptyState.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("emptyState.subtitle")}
              </Typography>
            </Box>
          </Stack>

          {/* Ações principais */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            mt={3}
            useFlexGap
          >
            <Button
              className="tour-chat-empty-cta"
              size="large"
              variant="contained"
              onClick={onNewConversation}
              startIcon={
                isCreating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RocketLaunchRoundedIcon />
                )
              }
              disabled={isCreating}
              sx={{ borderRadius: 2, fontWeight: 700, minWidth: 280 }}
            >
              {isCreating ? t("emptyState.ctaCreating") : t("emptyState.ctaFirst")}
            </Button>

            <Button
              size="large"
              variant="text"
              startIcon={<TuneRoundedIcon />}
              component={MuiLink}
              href="/settings/channels"
              sx={{ borderRadius: 2 }}
            >
              {t("emptyState.connectChannels")}
            </Button>
          </Stack>

          {/* Badges */}
          <Stack direction="row" spacing={1} mt={2}>
            <Chip
              size="small"
              variant="outlined"
              icon={<AutoAwesomeRoundedIcon fontSize="small" />}
              label="Omnichannel"
            />
            <Chip size="small" variant="outlined" label="Colaborativo" />
            <Chip size="small" variant="outlined" label="Rastreamento completo" />
          </Stack>
        </Box>

        <Divider />

        {/* Blocos informativos */}
        <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <InfoCard
                icon={<ForumRoundedIcon />}
                title={t("emptyState.flowTitle")}
                bullets={[
                  t("emptyState.flowB1"),
                  t("emptyState.flowB2"),
                  t("emptyState.flowB3"),
                ]}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InfoCard
                icon={<AccountTreeRoundedIcon />}
                title={t("emptyState.productivityTitle")}
                bullets={[
                  t("emptyState.productivityB1"),
                  t("emptyState.productivityB2"),
                  t("emptyState.productivityB3"),
                ]}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InfoCard
                icon={<SendRoundedIcon />}
                title={t("emptyState.quickStartTitle")}
                bullets={[
                  t("emptyState.quickStartB1"),
                  t("emptyState.quickStartB2"),
                  t("emptyState.quickStartB3"),
                ]}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmptyState;
